import React, { useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { setSnackBar } from "../../../store/common/commonSlice";
import {
    useCancelShopOrderMutation,
    useGetShopOrderQuery,
    useShipShopOrderMutation,
} from "../../../store/shopAdmin/shopAdminApis";
import { handleDateTimeDefault } from "../../../helpers/utils";
import { STATUS_META } from "./shopOrderStatus";

const rupees = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const Line = ({ label, value, bold, green }) => (
    <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
        <Typography variant="body2" fontWeight={bold ? 700 : 400}>
            {label}
        </Typography>
        <Typography variant="body2" fontWeight={bold ? 700 : 500} color={green ? "success.main" : "text.primary"}>
            {value}
        </Typography>
    </Box>
);

const ShopOrderDetailsDialog = ({ orderId, onClose }) => {
    const dispatch = useDispatch();
    const { data: order, isFetching, error } = useGetShopOrderQuery(orderId, { skip: !orderId });
    const [cancelOrder, { isLoading: cancelling }] = useCancelShopOrderMutation();
    const [shipOrder, { isLoading: shipping }] = useShipShopOrderMutation();

    const handleShip = async () => {
        try {
            const result = await shipOrder(orderId).unwrap();
            dispatch(
                setSnackBar({
                    open: true,
                    message:
                        result?.action === "created"
                            ? `Booked with GoSwift. AWB ${result.awb_code} (${result.courier_name})`
                            : `Tracking refreshed (${result?.updated || 0} new updates)`,
                    severity: "success",
                }),
            );
        } catch (e) {
            dispatch(setSnackBar({ open: true, message: e?.data?.message || "GoSwift request failed", severity: "error" }));
        }
    };
    const [showCancel, setShowCancel] = useState(false);
    const [reason, setReason] = useState("");

    const handleCancel = async () => {
        try {
            const result = await cancelOrder({ id: orderId, reason }).unwrap();
            dispatch(
                setSnackBar({
                    open: true,
                    message: result?.refund_note ? `Order cancelled. ${result.refund_note}` : "Order cancelled",
                    severity: "success",
                }),
            );
            setShowCancel(false);
            setReason("");
        } catch (e) {
            dispatch(setSnackBar({ open: true, message: e?.data?.message || "Could not cancel order", severity: "error" }));
        }
    };

    const status = order ? STATUS_META[order.status] || { label: order.status, color: "default" } : null;
    const a = order?.shipping_address;
    const snapshot = order?.settings_snapshot;

    return (
        <Dialog open={Boolean(orderId)} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Stack direction="row" spacing={1} alignItems="center">
                    <span>{order?.order_number || "Shop order"}</span>
                    {status ? <Chip size="small" label={status.label} color={status.color} /> : null}
                </Stack>
            </DialogTitle>
            <DialogContent dividers>
                {isFetching && <CircularProgress />}
                {error && <Alert severity="error">{error?.data?.message || "Could not load order"}</Alert>}
                {order && !isFetching && (
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Customer
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                    {order.customer?.name}
                                </Typography>
                                <Typography variant="body2">+91 {order.customer?.mobile}</Typography>
                                {order.customer?.email ? <Typography variant="body2">{order.customer.email}</Typography> : null}
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                    Customer Master ID: {order.customer?.customer_id || "—"} · Referral code {order.customer?.referral_code}
                                </Typography>
                                {order.referrer ? (
                                    <Typography variant="caption" color="info.main" display="block">
                                        Referred by {order.referrer.name} ({order.referrer.referral_code})
                                    </Typography>
                                ) : null}
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Deliver to
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                    {a?.name} · +91 {a?.phone}
                                </Typography>
                                <Typography variant="body2">
                                    {[a?.address_line1, a?.address_line2, a?.landmark].filter(Boolean).join(", ")}
                                </Typography>
                                <Typography variant="body2">
                                    {a?.city}, {a?.state} - {a?.pincode}
                                </Typography>
                            </Paper>
                        </Grid>

                        <Grid item xs={12}>
                            <Paper variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Product</TableCell>
                                            <TableCell align="right">MRP</TableCell>
                                            <TableCell align="right">Price</TableCell>
                                            <TableCell align="right">Qty</TableCell>
                                            <TableCell align="right">Total</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {order.items.map((i) => (
                                            <TableRow key={i.id}>
                                                <TableCell>{i.product_name}</TableCell>
                                                <TableCell align="right">{i.unit_mrp ? rupees(i.unit_mrp) : "—"}</TableCell>
                                                <TableCell align="right">{rupees(i.unit_price)}</TableCell>
                                                <TableCell align="right">{i.quantity}</TableCell>
                                                <TableCell align="right">{rupees(i.line_total)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Paper>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Amount (frozen at order time)
                                </Typography>
                                <Line label="Subtotal" value={rupees(order.subtotal)} />
                                {Number(order.first_order_discount) > 0 ? (
                                    <Line label="First order offer" value={`− ${rupees(order.first_order_discount)}`} green />
                                ) : null}
                                {Number(order.coupon_discount) > 0 ? (
                                    <Line label={`Coupon ${order.coupon_code}`} value={`− ${rupees(order.coupon_discount)}`} green />
                                ) : null}
                                <Line label="Delivery" value={Number(order.shipping_fee) ? rupees(order.shipping_fee) : "FREE"} />
                                <Divider sx={{ my: 1 }} />
                                <Line label="Total" value={rupees(order.total_amount)} bold />
                                {snapshot?.discount_note ? (
                                    <Typography variant="caption" color="text.secondary">
                                        {snapshot.discount_note}
                                    </Typography>
                                ) : null}
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Payments
                                </Typography>
                                {order.payments.length ? (
                                    order.payments.map((p) => (
                                        <Box key={p.id} sx={{ mb: 1 }}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Chip
                                                    size="small"
                                                    label={p.status}
                                                    color={p.status === "success" ? "success" : p.status === "failed" ? "error" : "default"}
                                                />
                                                <Typography variant="body2">{rupees(p.amount)}</Typography>
                                            </Stack>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                {p.merchant_transaction_id}
                                                {p.payment_instrument ? ` · ${p.payment_instrument}` : ""} · {handleDateTimeDefault(p.created_at)}
                                            </Typography>
                                        </Box>
                                    ))
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        No payment attempts
                                    </Typography>
                                )}
                                <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>
                                    Shipment
                                </Typography>
                                {order.shipment?.awb_code ? (
                                    <>
                                        <Typography variant="body2">
                                            {order.shipment.courier_name} · AWB{" "}
                                            {order.shipment.tracking_url ? (
                                                <a href={order.shipment.tracking_url} target="_blank" rel="noreferrer">
                                                    {order.shipment.awb_code}
                                                </a>
                                            ) : (
                                                order.shipment.awb_code
                                            )}
                                        </Typography>
                                        <Box sx={{ mt: 1, maxHeight: 180, overflow: "auto" }}>
                                            {[...(order.shipment.events || [])].reverse().map((e) => (
                                                <Box key={e.id} sx={{ borderLeft: "2px solid #e0e0e0", pl: 1.5, pb: 1 }}>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {e.courier_status || e.status}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {handleDateTimeDefault(e.event_time)}
                                                        {e.location ? ` · ${e.location}` : ""}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </>
                                ) : (
                                    <Typography variant="body2" color={order.shipment?.last_error ? "error" : "text.secondary"}>
                                        {order.shipment?.last_error
                                            ? `GoSwift error (attempt ${order.shipment.create_attempts}): ${order.shipment.last_error}`
                                            : order.status === "pending_payment" || order.status === "cancelled"
                                            ? "Not shipped"
                                            : "Waiting to be booked with GoSwift"}
                                    </Typography>
                                )}
                                {["paid", "confirmed", "packed", "shipped", "in_transit", "out_for_delivery", "failed"].includes(order.status) ? (
                                    <Button size="small" variant="outlined" sx={{ mt: 1 }} disabled={shipping} onClick={handleShip}>
                                        {order.shipment?.awb_code ? "Refresh tracking" : "Book with GoSwift now"}
                                    </Button>
                                ) : null}
                            </Paper>
                        </Grid>

                        {order.status === "cancelled" ? (
                            <Grid item xs={12}>
                                <Alert severity="warning">
                                    Cancelled {order.cancelled_at ? handleDateTimeDefault(order.cancelled_at) : ""}: {order.cancel_reason}
                                </Alert>
                            </Grid>
                        ) : null}

                        {showCancel ? (
                            <Grid item xs={12}>
                                <Paper variant="outlined" sx={{ p: 2, borderColor: "error.light" }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Cancel this order
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        {order.status === "pending_payment"
                                            ? "The customer has not paid, so no refund is needed."
                                            : `The customer paid ${rupees(order.total_amount)}. Refund it from the Razorpay dashboard after cancelling.`}
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Reason (visible in order history)"
                                        value={reason}
                                        onChange={(e) => setReason(e.target.value)}
                                    />
                                    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                                        <Button
                                            variant="contained"
                                            color="error"
                                            disabled={reason.trim().length < 3 || cancelling}
                                            onClick={handleCancel}
                                        >
                                            Confirm cancel
                                        </Button>
                                        <Button onClick={() => setShowCancel(false)}>Keep order</Button>
                                    </Stack>
                                </Paper>
                            </Grid>
                        ) : null}
                    </Grid>
                )}
            </DialogContent>
            <DialogActions>
                {order?.can_cancel && !showCancel ? (
                    <Button color="error" onClick={() => setShowCancel(true)}>
                        Cancel order
                    </Button>
                ) : null}
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default ShopOrderDetailsDialog;
